import { clearAuth, getAccessToken, getRefreshToken, updateTokens } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface PaginatedResponse<T = unknown> {
  success: boolean;
  data?: T[];
  error?: string;
  pagination?: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/customer/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      const body = await res.json();
      if (!res.ok || !body.success || !body.data) {
        return false;
      }

      updateTokens(body.data.accessToken, body.data.refreshToken, { emit: false });
      return true;
    } catch {
      return false;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function requestRaw(path: string, init: RequestInit = {}, retryOnUnauthorized = true): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = getAccessToken();
  const hasSession = !!token || !!getRefreshToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (res.status === 401 && hasSession) {
    if (retryOnUnauthorized) {
      const refreshed = await refreshSession();
      if (refreshed) {
        return requestRaw(path, init, false);
      }
    }

    clearAuth();
    throw new Error('Unauthorized');
  }

  return res;
}

async function requestJson<T>(path: string, init: RequestInit = {}, retryOnUnauthorized = true): Promise<T> {
  const res = await requestRaw(path, init, retryOnUnauthorized);
  return res.json() as Promise<T>;
}

export async function apiGet<T>(path: string, params?: Record<string, string | number | undefined>): Promise<PaginatedResponse<T>> {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    for (const [key, val] of Object.entries(params)) {
      if (val !== undefined && val !== '') url.searchParams.set(key, String(val));
    }
  }
  return requestJson<PaginatedResponse<T>>(`${path}${url.search}`, { method: 'GET' });
}

export async function apiPost<T>(path: string, body: Record<string, unknown>): Promise<ApiResponse<T>> {
  return requestJson<ApiResponse<T>>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function apiDelete<T>(path: string): Promise<ApiResponse<T>> {
  return requestJson<ApiResponse<T>>(path, {
    method: 'DELETE',
  });
}

export async function apiGetOne<T>(path: string, params?: Record<string, string | number | undefined>): Promise<ApiResponse<T>> {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    for (const [key, val] of Object.entries(params)) {
      if (val !== undefined && val !== '') url.searchParams.set(key, String(val));
    }
  }

  return requestJson<ApiResponse<T>>(`${path}${url.search}`, { method: 'GET' });
}

export async function apiPostFormData<T>(path: string, formData: FormData): Promise<ApiResponse<T>> {
  return requestJson<ApiResponse<T>>(path, {
    method: 'POST',
    body: formData,
  });
}

export async function apiGetBlob(path: string): Promise<Blob> {
  const res = await requestRaw(path, { method: 'GET' });

  if (!res.ok) {
    throw new Error(`Request failed with status ${res.status}`);
  }

  return res.blob();
}
